import { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import FormInput from "../../../../components/Form/FormInput";
import DateTimeField from "../../../../components/Form/DateTimeField";
import FormSelect from "../../../../components/Form/FormSelect";
import TextEditor from "../../../../components/Form/TextEditor";
import FormTextarea from "../../../../components/Form/TextArea";
import TagMultiSelect from "../../../../components/Form/TagMultiSelect";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance, isProjectManager } from "../../../../utils/constants/accountType";
import { btn } from "../../../../components/Button/buttonStyles";

const CreateProjectForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers - the manager fields are hidden and
  // the primary-manager requirement is skipped for them.
  const freelance = isFreelance(user);
  const projectManager = isProjectManager(user);
  const [searchParams] = useSearchParams();
  const presetClientId = searchParams.get("clientId") || "";
  const isClientLocked = Boolean(presetClientId);

  const getLocalDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    const localTime = new Date(now.getTime() - offset);
    return localTime.toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    client_id: presetClientId,
    client_contact_person_id: "",
    project_name: "",
    service_id: "",
    language_pair_ids: [],
    specialization_id: "",
    start_at: getLocalDateTime(),
    deadline_at: "",
    instructions: "",
    internal_note: "",
    primary_manager_id: "",
    secondary_manager_id: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);
  const [services, setServices] = useState([]);
  const [languagePairs, setLanguagePairs] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get("/clients", { withCredentials: true });
        setClients(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch clients:", err.response || err);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.get("/admin-services", {
          withCredentials: true,
        });

        const activeServices = (res.data.data || []).filter(
          (s) => s.active_flag === true
        );

        setServices(activeServices);
      } catch (err) {
        console.error("Failed to fetch services:", err.response || err);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const fetchLanguagePairs = async () => {
      try {
        const res = await api.get("/admin-language-pairs", {
          withCredentials: true,
        });
        setLanguagePairs(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch language pairs:", err.response || err);
      }
    };

    fetchLanguagePairs();
  }, []);

  useEffect(() => {
    const fetchSpecializations = async () => {
      try {
        const res = await api.get("/admin-specializations", {
          withCredentials: true,
        });

        const activeSpecializations = (res.data.data || []).filter(
          (s) => s.active_flag === true
        );

        setSpecializations(activeSpecializations);
      } catch (err) {
        console.error("Failed to fetch specializations:", err.response || err);
      }
    };

    fetchSpecializations();
  }, []);

  useEffect(() => {
    // Freelance accounts have no managers (the endpoint is enterprise-only).
    if (freelance) return;

    const fetchManagers = async () => {
      try {
        // The full manager module is admin-only; project managers read the
        // trimmed roster of their admin's project managers instead, and are
        // preselected as the primary manager of the projects they create.
        const res = await api.get(
          projectManager ? "/assignable-managers" : "/managers",
          { withCredentials: true }
        );
        setManagers(res.data.data || []);

        const selfId = res.data.meta?.self_manager_id;
        if (selfId) {
          setForm((prev) =>
            prev.primary_manager_id
              ? prev
              : { ...prev, primary_manager_id: String(selfId) }
          );
        }
      } catch (err) {
        console.error("Failed to fetch managers:", err.response || err);
      }
    };

    fetchManagers();
  }, [freelance, projectManager]);

  useEffect(() => {
    const fetchContactPersons = async () => {
      if (!form.client_id) {
        setContactPersons([]);
        setForm((prev) => ({
          ...prev,
          client_contact_person_id: "",
        }));
        return;
      }

      try {
        const res = await api.get(`/client/contact-persons/${form.client_id}`, {
          withCredentials: true,
        });

        setContactPersons(res.data.data || []);

        setForm((prev) => ({
          ...prev,
          client_contact_person_id: "",
        }));
      } catch (err) {
        console.error("Failed to fetch contact persons:", err.response || err);
        setContactPersons([]);
      }
    };

    fetchContactPersons();
  }, [form.client_id]);

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;

    if (type === "select-multiple") {
      const values = Array.from(selectedOptions).map((opt) =>
        Number(opt.value)
      );
      setForm((prev) => ({ ...prev, [name]: values }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const validate = () => {
    const err = {};
    if (!form.client_id) err.client_id = "Client is required.";
    if (!form.project_name?.trim()) err.project_name = "Project name is required.";
    if (!form.start_at) err.start_at = "Start date is required.";
    if (!form.deadline_at) err.deadline_at = "Deadline is required.";
    if (!freelance && !form.primary_manager_id)
      err.primary_manager_id = "Primary manager is required.";
    
    // Add validation for language pairs
    if (!form.language_pair_ids || form.language_pair_ids.length === 0) {
      err.language_pair_ids = "At least one language pair is required.";
    }

    // Validate deadline is after start date
    if (form.start_at && form.deadline_at && form.deadline_at <= form.start_at) {
      err.deadline_at = "Deadline must be after start date.";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setServerError("");

    try {
      // Prepare the payload
      const payload = {
        ...form,
        // Convert empty strings to null for optional fields
        client_contact_person_id: form.client_contact_person_id || null,
        service_id: form.service_id || null,
        specialization_id: form.specialization_id || null,
        secondary_manager_id: form.secondary_manager_id || null,
        instructions: form.instructions || null,
        internal_note: form.internal_note || null,
      };

      await api.post("/projects", payload, {
        withCredentials: true 
      });

      setSuccess("Project created successfully!");
      
      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate("/projects");
      }, 1500);
    } catch (err) {
      console.error("Create project error:", err);
      setServerError(
        err.response?.data?.message || "Failed to create project."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow rounded-lg p-8 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormSelect
          label="Client"
          name="client_id"
          value={form.client_id}
          onChange={handleChange}
          options={clients.map((client) => ({
            value: client.id,
            label:
              client.type === "Company"
                ? client.company_name
                : `${client.primary_user?.first_name || ""} ${
                    client.primary_user?.last_name || ""
                  }`,
          }))}
          error={errors.client_id}
          required
          disabled={isClientLocked}
        />

        <FormSelect
          label="Client Contact Person"
          name="client_contact_person_id"
          value={form.client_contact_person_id}
          onChange={handleChange}
          options={contactPersons.map((p) => ({
            value: p.id,
            label: `${p.first_name} ${p.last_name}`,
          }))}
          disabled={!form.client_id}
        />

        <FormInput
          label="Project Name"
          name="project_name"
          value={form.project_name}
          onChange={handleChange}
          error={errors.project_name}
          required
        />

        <FormSelect
          label="Service"
          name="service_id"
          value={form.service_id}
          onChange={handleChange}
          options={services.map((s) => ({
            value: s.id,
            label: s.name,
          }))}
        />

        <div className="md:col-span-2">
          <TagMultiSelect
            label="Language Pairs"
            required
            value={form.language_pair_ids}
            onChange={(ids) => {
              setForm((prev) => ({ ...prev, language_pair_ids: ids }));
              setErrors((prev) => ({ ...prev, language_pair_ids: "" }));
            }}
            options={languagePairs.map((lp) => ({
              value: lp.id,
              label: `${lp.sourceLanguage?.name} → ${lp.targetLanguage?.name}`,
            }))}
            placeholder="Select language pairs"
            error={errors.language_pair_ids}
            emptyMessage="No language pairs configured"
          />
        </div>

        <FormSelect
          label="Specialization"
          name="specialization_id"
          value={form.specialization_id}
          onChange={handleChange}
          options={specializations.map((s) => ({
            value: s.id,
            label: s.name,
          }))}
        />

        <DateTimeField
          label="Start At"
          name="start_at"
          value={form.start_at}
          onChange={handleChange}
          error={errors.start_at}
          required
        />

        <DateTimeField
          label="Deadline At"
          name="deadline_at"
          value={form.deadline_at}
          onChange={handleChange}
          error={errors.deadline_at}
          min={form.start_at}
          required
        />

        {/* Managers are enterprise-only; hidden for freelance accounts. */}
        {!freelance && (
          <>
            <FormSelect
              label="Primary Manager"
              name="primary_manager_id"
              value={form.primary_manager_id}
              onChange={handleChange}
              options={managers.map((m) => ({
                value: m.id,
                label: `${m.first_name} ${m.last_name}`,
              }))}
              error={errors.primary_manager_id}
              required
            />

            <FormSelect
              label="Secondary Manager"
              name="secondary_manager_id"
              value={form.secondary_manager_id}
              onChange={handleChange}
              options={managers
                .filter((m) => m.id !== Number(form.primary_manager_id))
                .map((m) => ({
                  value: m.id,
                  label: `${m.first_name} ${m.last_name}`,
                }))}
            />
          </>
        )}
      </div>

      <TextEditor
        label="Instructions"
        value={form.instructions}
        onChange={(val) =>
          setForm((prev) => ({
            ...prev,
            instructions: val,
          }))
        }
      />

      <FormTextarea
        label="Internal Note"
        name="internal_note"
        value={form.internal_note}
        onChange={handleChange}
        rows={3}
        placeholder="Internal notes (not visible to client)"
      />

      {serverError && (
        <div className="bg-danger-100 text-danger-700 border border-danger-400 rounded p-3 text-center font-semibold">
          {serverError}
        </div>
      )}

      {success && (
        <div className="bg-green-100 text-green-700 border border-green-400 rounded p-3 text-center font-semibold">
          {success}
        </div>
      )}

      <span className="text-gray-500 text-sm mt-1">
        Fields marked with <span className="text-danger-600">*</span> are
        mandatory.
      </span>

      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className={btn("primary", "sm")}
        >
          {isSubmitting ? "Creating..." : "Submit"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/projects")}
          disabled={isSubmitting}
          className={btn("secondary", "sm")}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CreateProjectForm;